<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { MESSAGE_VARIABLES } from 'shared/constants/messages';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const state = reactive({
  params: {},
});

const activeTokenField = ref(null);

const MOCK_VALUES = {
  'contact.name': 'John Doe',
  'contact.first_name': 'John',
  'contact.last_name': 'Doe',
  'contact.email': 'john@example.com',
  'contact.phone': '+989123456789',
  'agent.name': 'Agent Name',
  'agent.first_name': 'Agent',
  'agent.last_name': 'Name',
  'agent.email': 'agent@example.com',
  'inbox.name': 'Kavenegar Inbox',
  'inbox.id': '1',
  'conversation.id': '100',
  'contact.id': '50',
};

const tokens = computed(() => {
  return props.template?.tokens || [];
});

const renderedTemplate = computed(() => {
  let content = props.template?.content || '';
  // Sort tokens by length descending to prevent %token matching inside %token2, %token10 etc.
  const sortedTokens = [...tokens.value].sort((a, b) => b.length - a.length);

  sortedTokens.forEach(token => {
    let value = state.params[token] || `{{ ${token} }}`;

    // Resolve system variables for preview
    Object.keys(MOCK_VALUES).forEach(varKey => {
      const placeholder = `{{ ${varKey} }}`;
      if (value.includes(placeholder)) {
        value = value.replace(
          new RegExp(`\\{\\{\\s*${varKey}\\s*\\}\\}`, 'g'),
          MOCK_VALUES[varKey]
        );
      }
    });

    content = content.replace(new RegExp(`%${token}`, 'g'), value);
  });
  return content;
});

const processedParams = computed(() => {
  return state.params;
});

const insertVariable = variableKey => {
  if (activeTokenField.value) {
    const currentVal = state.params[activeTokenField.value] || '';
    state.params[activeTokenField.value] = `${currentVal}{{ ${variableKey} }}`;
  }
};

defineExpose({
  renderedTemplate,
  processedParams,
});
</script>

<template>
  <div>
    <div v-if="tokens.length">
      <div
        class="flex flex-col gap-4 p-4 rounded-lg bg-n-alpha-2 border border-n-weak"
      >
        <div class="flex justify-between items-center">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.SMS.CREATE.FORM.TEMPLATE.VARIABLES_LABEL') }}
          </h4>
        </div>
        <div v-for="token in tokens" :key="token" class="flex flex-col gap-1">
          <Input
            v-model="state.params[token]"
            :label="token"
            :placeholder="
              t('CAMPAIGN.SMS.CREATE.FORM.TEMPLATE.VARIABLE_PLACEHOLDER', {
                variable: token,
              })
            "
            @focus="activeTokenField = token"
          />
        </div>

        <!-- Variable Picker Helper -->
        <div class="flex flex-wrap gap-1.5 mt-2">
          <p class="w-full text-[10px] font-semibold text-n-slate-11 uppercase">
            {{
              t('CAMPAIGN.SMS.CREATE.FORM.TEMPLATE.SYSTEM_VARIABLES_HELPER') ||
              'System Variables (click to insert)'
            }}
          </p>
          <Button
            v-for="variable in MESSAGE_VARIABLES"
            :key="variable.key"
            variant="faded"
            color="slate"
            size="xs"
            class="!text-[10px] !px-2 !h-6"
            :label="variable.label"
            @click="insertVariable(variable.key)"
          />
        </div>

        <div class="mt-4 pt-4 border-t border-n-weak">
          <p class="text-xs font-semibold text-n-slate-11 mb-2 uppercase">
            {{ t('CAMPAIGN.SMS.CREATE.FORM.TEMPLATE.PREVIEW') }}
          </p>
          <div
            class="p-4 rounded-xl bg-n-surface-2 text-sm text-n-slate-12 leading-relaxed italic border border-n-weak shadow-sm"
          >
            {{ renderedTemplate }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
